import React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";

const OPTS = [["current", "Still with the agency"], ["changes", "Details need correcting"], ["left", "No longer with the agency"]];
const DEFAULT_POSITIONS = ["P&C Producer / Sales Employee", "Life Insurance Sales Employee", "Customer Service Representative", "Office Manager", "Marketing", "Administrative Support", "Other"];
const REL = [["employee", "Employee (Agent Staff)"], ["producer", "Contracted Producer"]];

const sel = "h-9 rounded-md border border-slate-200 bg-white px-2 text-sm disabled:opacity-60";
const Tick = ({ label, checked, onChange, disabled, testid }) => (
  <label className="flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />{label}</label>
);

// Classification + licensing details shared by existing and newly added people.
function Details({ v, set, disabled, positions, tid }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
      <select className={sel} value={v.relationship || ""} disabled={disabled} onChange={(e) => set({ relationship: e.target.value })} data-testid={`${tid}-rel`}>
        <option value="">Agency relationship…</option>{REL.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <select className={sel} value={v.position || ""} disabled={disabled} onChange={(e) => set({ position: e.target.value })} data-testid={`${tid}-position`}>
        <option value="">Position / role…</option>{positions.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
      {v.relationship === "producer" ? <span className="text-xs text-slate-400 self-center">Contractor — not an employee</span> : (
        <select className={sel} value={v.employment || ""} disabled={disabled} onChange={(e) => set({ employment: e.target.value })} data-testid={`${tid}-employment`}>
          <option value="">Full-time / part-time…</option><option value="full_time">Full-Time</option><option value="part_time">Part-Time</option>
        </select>)}
      <div className="sm:col-span-3 flex flex-wrap gap-4">
        <Tick label="Remote worker" checked={v.remote} onChange={(c) => set({ remote: c })} disabled={disabled} testid={`${tid}-remote`} />
        <Tick label="P&C licensed" checked={v.pc_licensed} onChange={(c) => set({ pc_licensed: c })} disabled={disabled} testid={`${tid}-pc`} />
        <Tick label="Life & Health licensed" checked={v.lh_licensed} onChange={(c) => set({ lh_licensed: c })} disabled={disabled} testid={`${tid}-lh`} />
      </div>
    </div>
  );
}

function Existing({ s, item, setItem, disabled, positions }) {
  const v = { relationship: s.kind, ...item };
  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid={`staff-row-${s.id}`}>
      <p className="text-sm font-medium text-slate-900">{s.first_name} {s.last_name}
        <span className={`ml-2 text-[11px] rounded-full px-2 py-0.5 ${s.kind === "producer" ? "bg-violet-50 text-violet-700" : "bg-sky-50 text-sky-700"}`}>{s.kind === "producer" ? "Contracted Producer" : "Agent Staff"}</span>
        <span className="block text-xs font-normal text-slate-500">{[s.title, s.email, s.phone].filter(Boolean).join(" · ")}</span></p>
      <div className="flex flex-wrap gap-3 mt-2">
        {OPTS.map(([k, l]) => (
          <label key={k} className="flex items-center gap-1.5 text-sm"><input type="radio" disabled={disabled} checked={(item.status || "") === k}
            onChange={() => setItem({ status: k })} data-testid={`staff-${s.id}-${k}`} />{l}</label>
        ))}
      </div>
      {item.status !== "left" && <Details v={v} set={setItem} disabled={disabled} positions={positions} tid={`staff-${s.id}`} />}
      {item.status === "changes" && <Textarea rows={2} className="mt-2" disabled={disabled} placeholder="What needs to change?" value={item.note || ""}
        onChange={(e) => setItem({ note: e.target.value })} data-testid={`staff-note-${s.id}`} />}
    </div>
  );
}

function Added({ row, i, update, remove, disabled, positions }) {
  const set = (p) => update(i, { ...row, ...p });
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid={`staff-add-row-${i}`}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Input placeholder="First name" value={row.first_name || ""} disabled={disabled} onChange={(e) => set({ first_name: e.target.value })} data-testid={`staff-add-${i}-first`} />
        <Input placeholder="Last name" value={row.last_name || ""} disabled={disabled} onChange={(e) => set({ last_name: e.target.value })} data-testid={`staff-add-${i}-last`} />
        <div className="flex gap-1"><Input placeholder="Email" type="email" value={row.email || ""} disabled={disabled} onChange={(e) => set({ email: e.target.value })} data-testid={`staff-add-${i}-email`} />
          {!disabled && <Button type="button" variant="ghost" size="icon" onClick={() => remove(i)} data-testid={`staff-add-${i}-remove`}><Trash2 className="w-4 h-4" /></Button>}</div>
      </div>
      <Details v={row} set={set} disabled={disabled} positions={positions} tid={`staff-add-${i}`} />
    </div>
  );
}

// Read-only snapshot of existing staff/producer records. Corrections are collected with the survey and never change live records.
export function StaffReview({ staff, value, onChange, disabled, positions = DEFAULT_POSITIONS }) {
  const items = value.items || {};
  const added = value.added || [];
  const setItem = (id, patch) => onChange({ ...value, items: { ...items, [id]: { ...(items[id] || {}), ...patch } } });
  const update = (i, row) => onChange({ ...value, added: added.map((r, j) => (j === i ? row : r)), none_additional: false });
  const remove = (i) => onChange({ ...value, added: added.filter((_, j) => j !== i) });
  return (
    <div className="space-y-3" data-testid="staff-review">
      <p className="text-sm text-slate-600">These are the Agent Staff (employees) and Contracted Producers on file for your agency. Confirm each one and note any corrections. Your District Office reviews changes after you submit — nothing here updates your records automatically.</p>
      <p className="text-[11px] text-slate-400">Licensing indicators are self-reported and are not an official license verification.</p>
      {staff.length ? staff.map((s) => <Existing key={s.id} s={s} item={items[s.id] || {}} setItem={(p) => setItem(s.id, p)} disabled={disabled} positions={positions} />)
        : <p className="text-sm text-slate-400">No staff or producers are on file for your agency.</p>}
      <div className="space-y-2">
        <p className="text-sm font-medium text-slate-700">Staff or producers not listed above</p>
        {added.map((r, i) => <Added key={i} row={r} i={i} update={update} remove={remove} disabled={disabled} positions={positions} />)}
        {!disabled && <Button type="button" variant="outline" size="sm" onClick={() => onChange({ ...value, added: [...added, {}], none_additional: false })} data-testid="staff-add-btn"><Plus className="w-4 h-4 mr-1" />Add a person</Button>}
        {!added.length && <Tick label="My agency has no additional staff or producers" checked={value.none_additional} disabled={disabled} onChange={(c) => onChange({ ...value, none_additional: c })} testid="staff-none-additional" />}
        {value.additions && <p className="text-xs text-slate-500">Earlier note: {value.additions}</p>}
      </div>
    </div>
  );
}
